import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const STATUS_COLOR = {
  confirmed: '#3b82f6', attended: '#10b981',
  not_attended: '#6b7280', cancelled: '#ef4444', rescheduled: '#8b5cf6',
};
const STATUS_LABEL = {
  confirmed: 'Confirmada', attended: 'Atendida',
  not_attended: 'No asistió', cancelled: 'Cancelada', rescheduled: 'Reagendada',
};

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const toDS = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const todayStr = new Date().toISOString().split('T')[0];

const getMonday = d => {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const m = new Date(d);
  m.setDate(d.getDate() + diff);
  return m;
};

export default function CalendarPage() {
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allAppts, setAllAppts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [filterWorker, setFilterWorker] = useState('');

  const getRange = (v, d) => {
    if (v === 'month') {
      const y = d.getFullYear(), m = d.getMonth();
      return {
        start: `${y}-${String(m+1).padStart(2,'0')}-01`,
        end: `${y}-${String(m+1).padStart(2,'0')}-${String(new Date(y,m+1,0).getDate()).padStart(2,'0')}`,
      };
    }
    if (v === 'week') {
      const mon = getMonday(d);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return { start: toDS(mon), end: toDS(sun) };
    }
    const ds = toDS(d);
    return { start: ds, end: ds };
  };

  const loadAppts = useCallback(async (v, d) => {
    setLoading(true);
    try {
      const { start, end } = getRange(v, d);
      const res = await api.get('/appointments/range', { params: { start, end } });
      setAllAppts(res.data || []);
    } catch { setAllAppts([]); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { loadAppts(view, currentDate); }, [view, currentDate]); // eslint-disable-line

  useEffect(() => {
    api.get('/workers').then(r => setWorkers(r.data)).catch(() => {});
  }, []);

  const filtered = allAppts.filter(a => !filterWorker || String(a.worker_id) === String(filterWorker));

  const byDate = {};
  filtered.forEach(a => {
    const d = a.appointment_date?.slice(0,10);
    if (!d) return;
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(a);
  });

  const navigate = dir => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
    setSelectedDate(null);
  };

  const goToday = () => { setCurrentDate(new Date()); setSelectedDate(todayStr); };

  const switchView = v => {
    if (v === 'day' && selectedDate) setCurrentDate(new Date(selectedDate + 'T12:00:00'));
    setView(v);
    setSelectedDate(null);
  };

  const getHeaderLabel = () => {
    if (view === 'month') return `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (view === 'week') {
      const mon = getMonday(currentDate);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return `${mon.getDate()} ${MONTH_NAMES[mon.getMonth()].slice(0,3)} – ${sun.getDate()} ${MONTH_NAMES[sun.getMonth()].slice(0,3)} ${sun.getFullYear()}`;
    }
    return currentDate.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const dayAppts = selectedDate
    ? (byDate[selectedDate] || []).sort((a,b) => (a.start_time||'').localeCompare(b.start_time||''))
    : [];

  /* ── MONTH VIEW ── */
  const renderMonth = () => {
    const y = currentDate.getFullYear(), m = currentDate.getMonth();
    const first = new Date(y, m, 1).getDay();
    const total = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let i = 1; i <= total; i++) cells.push(i);

    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4 }}>
          {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-faint)', padding: '4px 0', textTransform: 'uppercase' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const ds = `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
            const appts = byDate[ds] || [];
            const isToday = ds === todayStr;
            const isSel = ds === selectedDate;
            return (
              <div key={i} onClick={() => setSelectedDate(isSel ? null : ds)} style={{
                minHeight: 80, borderRadius: 8, padding: '6px 8px', cursor: 'pointer',
                background: isSel ? 'rgba(201,168,76,0.15)' : isToday ? 'rgba(201,168,76,0.06)' : 'var(--color-surface)',
                border: isSel ? '2px solid var(--color-gold)' : isToday ? '1px solid var(--color-gold)' : '1px solid var(--color-border)',
                transition: 'all 0.15s',
              }}>
                <div style={{ fontWeight: isToday ? 700 : 500, fontSize: '0.85rem', marginBottom: 4, color: isToday ? 'var(--color-gold)' : 'var(--color-text)' }}>{d}</div>
                {appts.slice(0, 3).map(a => (
                  <div key={a.id} style={{
                    fontSize: '0.65rem', padding: '1px 4px', borderRadius: 3, marginBottom: 2,
                    background: (STATUS_COLOR[a.status] || '#888') + '33',
                    color: STATUS_COLOR[a.status] || '#888',
                    fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {a.start_time?.slice(0,5)} {a.client_name}
                  </div>
                ))}
                {appts.length > 3 && <div style={{ fontSize: '0.62rem', color: 'var(--color-text-muted)' }}>+{appts.length - 3} más</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ── WEEK VIEW ── */
  const renderWeek = () => {
    const monday = getMonday(currentDate);
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday); d.setDate(monday.getDate() + i); return d;
    });
    const dayNames = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 6 }}>
        {days.map((d, i) => {
          const ds = toDS(d);
          const appts = (byDate[ds] || []).sort((a,b) => (a.start_time||'').localeCompare(b.start_time||''));
          const isToday = ds === todayStr;
          const isSel = ds === selectedDate;
          return (
            <div key={i} onClick={() => setSelectedDate(isSel ? null : ds)} style={{
              borderRadius: 10, padding: '10px 6px', cursor: 'pointer', minHeight: 150,
              background: isSel ? 'rgba(201,168,76,0.12)' : 'var(--color-surface)',
              border: isSel ? '2px solid var(--color-gold)' : isToday ? '1px solid var(--color-gold)' : '1px solid var(--color-border)',
              transition: 'all 0.15s',
            }}>
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase' }}>{dayNames[i]}</div>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', margin: '3px auto 0',
                  background: isToday ? 'var(--color-gold)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '0.9rem',
                  color: isToday ? '#fff' : 'var(--color-text)',
                }}>{d.getDate()}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {appts.slice(0, 5).map(a => (
                  <div key={a.id} style={{
                    fontSize: '0.65rem', padding: '3px 5px', borderRadius: 5,
                    background: (STATUS_COLOR[a.status] || '#888') + '22',
                    borderLeft: `2px solid ${STATUS_COLOR[a.status] || '#888'}`,
                    lineHeight: 1.3,
                  }}>
                    <div style={{ fontWeight: 700, color: STATUS_COLOR[a.status] }}>{a.start_time?.slice(0,5)}</div>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--color-text)' }}>{a.client_name}</div>
                  </div>
                ))}
                {appts.length > 5 && <div style={{ fontSize: '0.6rem', color: 'var(--color-text-muted)', textAlign: 'center' }}>+{appts.length - 5} más</div>}
                {appts.length === 0 && <div style={{ fontSize: '0.65rem', color: 'var(--color-text-faint)', textAlign: 'center', marginTop: 8 }}>—</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /* ── DAY VIEW ── */
  const renderDay = () => {
    const ds = toDS(currentDate);
    const appts = (byDate[ds] || []).sort((a,b) => (a.start_time||'').localeCompare(b.start_time||''));

    if (!loading && appts.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-text-muted)' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
          No hay citas para este día
        </div>
      );
    }

    const hours = Array.from({ length: 14 }, (_, i) => i + 7); // 07 – 20

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {hours.map(h => {
          const hStr = String(h).padStart(2, '0');
          const hourAppts = appts.filter(a => a.start_time?.slice(0,2) === hStr);
          return (
            <div key={h} style={{
              display: 'flex', gap: 14,
              borderTop: '1px solid var(--color-border)',
              padding: '8px 0', minHeight: hourAppts.length ? 'auto' : 44,
            }}>
              <div style={{ width: 44, flexShrink: 0, fontSize: '0.75rem', color: 'var(--color-text-faint)', paddingTop: 4, textAlign: 'right' }}>
                {hStr}:00
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {hourAppts.map(a => (
                  <div key={a.id} style={{
                    padding: '10px 14px', borderRadius: 10,
                    background: (STATUS_COLOR[a.status] || '#888') + '18',
                    borderLeft: `4px solid ${STATUS_COLOR[a.status] || '#888'}`,
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ flexShrink: 0, minWidth: 44, textAlign: 'center' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: STATUS_COLOR[a.status] }}>{a.start_time?.slice(0,5)}</div>
                      {a.end_time && <div style={{ fontSize: '0.7rem', color: 'var(--color-text-faint)' }}>{a.end_time.slice(0,5)}</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{a.client_name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                        {a.service_name}{a.worker_name ? ` · ✂️ ${a.worker_name}` : ''}
                        {a.price ? ` · $${Number(a.price).toLocaleString('es-CO')}` : ''}
                      </div>
                    </div>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 600, flexShrink: 0,
                      color: STATUS_COLOR[a.status],
                      background: (STATUS_COLOR[a.status] || '#888') + '22',
                      padding: '3px 10px', borderRadius: 20,
                    }}>{STATUS_LABEL[a.status]}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Calendario</h2>
          <p>Vista de citas por mes, semana o día</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="form-select" style={{ width: 'auto' }} value={filterWorker} onChange={e => setFilterWorker(e.target.value)}>
            <option value="">Todos los barberos</option>
            {workers.map(w => <option key={w.id} value={w.id}>{w.nombre || w.name}</option>)}
          </select>
          {/* Toggle Mes / Semana / Día */}
          <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden', background: 'var(--color-surface)' }}>
            {[['month','Mes'],['week','Semana'],['day','Día']].map(([v, label]) => (
              <button key={v} onClick={() => switchView(v)} style={{
                padding: '7px 18px', border: 'none', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: 600,
                background: view === v ? '#0f172a' : 'transparent',
                color: view === v ? '#fff' : 'var(--color-text-muted)',
                transition: 'all 0.15s',
              }}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: (view !== 'day' && selectedDate) ? '1fr 300px' : '1fr', gap: 20, alignItems: 'start' }}>
        <div className="card">
          {/* Nav header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <button className="btn btn-ghost" onClick={() => navigate(-1)}>‹ Anterior</button>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--color-gold)', textTransform: 'capitalize', textAlign: 'center' }}>
              {getHeaderLabel()}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={goToday}>Hoy</button>
              <button className="btn btn-ghost" onClick={() => navigate(1)}>Siguiente ›</button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : (
            <>
              {view === 'month' && renderMonth()}
              {view === 'week' && renderWeek()}
              {view === 'day' && renderDay()}
            </>
          )}

          {/* Leyenda */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: STATUS_COLOR[k], flexShrink: 0 }} />
                {v}
              </div>
            ))}
          </div>
        </div>

        {/* Panel día seleccionado (solo en mes y semana) */}
        {view !== 'day' && selectedDate && (
          <div className="card" style={{ alignSelf: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--color-gold)', textTransform: 'capitalize' }}>
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost btn-sm" title="Ver en vista día" onClick={() => switchView('day')}>↗ Día</button>
                <button className="btn btn-ghost btn-icon" onClick={() => setSelectedDate(null)}>✕</button>
              </div>
            </div>
            {dayAppts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Sin citas este día</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 520, overflowY: 'auto' }}>
                {dayAppts.map(a => (
                  <div key={a.id} style={{
                    padding: '10px 12px', background: 'var(--color-surface)', borderRadius: 8,
                    borderLeft: `3px solid ${STATUS_COLOR[a.status] || '#888'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--color-gold)' }}>{a.start_time?.slice(0,5)}</span>
                      <span style={{ fontSize: '0.72rem', color: STATUS_COLOR[a.status], fontWeight: 600 }}>{STATUS_LABEL[a.status]}</span>
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{a.client_name}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{a.service_name}</div>
                    {a.worker_name && <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>✂️ {a.worker_name}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
